// Call control through the bridge's non-UI InCallService, and listening to
// the far end (downlink audio, AGENTS.md §7.2). Speaking into the call needs
// a device with uplink injection or carrier forwarding (§7.3).

import { CallStateKind, type CallEvent } from "@fistos/shared";
import {
  Headphones,
  Mic,
  MicOff,
  Pause,
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneOff,
  Play,
  RefreshCw,
} from "lucide-solid";
import { createMemo, createSignal, For, onCleanup, Show } from "solid-js";
import { toast } from "somoto";
import { ToneBadge } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TextField, TextFieldInput } from "@/components/ui/text-field";
import { clock, humanize } from "@/lib/format";
import { live } from "@/lib/live";
import { MediaSession, webCodecsSupported } from "@/lib/media";
import { actions, parse } from "@/lib/proto";
import { OfflineNote, run, Unavailable, type PanelProps } from "./common";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

function stateName(s: CallStateKind): string {
  return humanize((CallStateKind[s] ?? "").replace(/^CALL_STATE_/, ""));
}

export function CallsPanel(props: PanelProps) {
  const id = () => props.device.id;
  const [number, setNumber] = createSignal("");
  const [polled, setPolled] = createSignal<CallEvent[] | null>(null);
  const [muted, setMuted] = createSignal(false);
  const [listening, setListening] = createSignal(false);
  let media: MediaSession | null = null;

  // Latest state per call from live events, overlaid on the last poll.
  const calls = createMemo(() => {
    const byId = new Map<string, CallEvent>();
    for (const c of polled() ?? []) byId.set(c.callId, c);
    for (const e of live.events[id()] ?? []) {
      if (e.kind !== "call") continue;
      const c = parse.call(e.data);
      byId.set(c.callId, c);
    }
    return [...byId.values()].filter((c) => c.state !== CallStateKind.CALL_STATE_DISCONNECTED);
  });
  const recent = createMemo(() =>
    (live.events[id()] ?? [])
      .filter((e) => e.kind === "call")
      .map((e) => ({ at: e.at, c: parse.call(e.data) }))
      .reverse()
      .slice(0, 8),
  );

  const refresh = async () => {
    const r = await run(id(), actions.call({ case: "getState", value: { $typeName: "fistos.v1.GetCallState" } }));
    if (r?.payload.case === "callState") {
      setPolled(r.payload.value.calls);
      setMuted(r.payload.value.muted);
    }
  };
  const op = (o: Parameters<typeof actions.call>[0], callId = "") => run(id(), actions.call(o, callId));

  const dial = async (e: Event) => {
    e.preventDefault();
    if (!number().trim()) return;
    await op({ case: "dial", value: { $typeName: "fistos.v1.Dial", number: number().trim() } });
  };
  const dtmf = (d: string) => op({ case: "dtmf", value: { $typeName: "fistos.v1.Dtmf", digits: d } });

  const listen = async (on: boolean) => {
    if (on) {
      if (!webCodecsSupported()) return toast.error("This browser cannot decode Opus (WebCodecs).");
      media = new MediaSession(id(), { audio: true, onError: (m) => toast.error(m) });
      media.start();
      const r = await op({
        case: "streamAudio",
        value: { $typeName: "fistos.v1.StreamCallAudio", enable: true, uplink: false },
      });
      if (!r) {
        media.stop();
        media = null;
        return;
      }
      setListening(true);
    } else {
      await op({
        case: "streamAudio",
        value: { $typeName: "fistos.v1.StreamCallAudio", enable: false, uplink: false },
      });
      media?.stop();
      media = null;
      setListening(false);
    }
  };
  onCleanup(() => {
    if (listening()) void listen(false);
  });

  const downlink = () => props.caps?.callDownlink || "none";

  return (
    <Show
      when={props.caps?.callControl !== false}
      fallback={
        <Unavailable title="Calls are not available">
          This phone reports no call control (no telephony, or the in-call service is not bound).
        </Unavailable>
      }
    >
      <div class="grid gap-4 *:min-w-0 lg:grid-cols-2">
        <Show when={!props.device.online}>
          <div class="lg:col-span-2">
            <OfflineNote device={props.device} />
          </div>
        </Show>
        <Card class="gap-4 py-5">
          <CardHeader class="px-5">
            <CardTitle class="flex items-center justify-between text-base">
              Current calls
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Refresh call state"
                onClick={refresh}
                disabled={!props.device.online}
              >
                <RefreshCw />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent class="grid gap-3 px-5 *:min-w-0">
            <Show when={calls().length} fallback={<p class="text-muted-foreground text-sm">No active calls.</p>}>
              <For each={calls()}>
                {(c) => (
                  <div class="grid gap-3 rounded-lg border p-3 *:min-w-0">
                    <div class="flex items-center gap-3">
                      <div class="bg-muted flex size-9 items-center justify-center rounded-full">
                        <Show when={c.incoming} fallback={<PhoneCall class="size-4" />}>
                          <PhoneIncoming class="size-4" />
                        </Show>
                      </div>
                      <div class="min-w-0 flex-1">
                        <div class="truncate font-medium">{c.contactName || c.number || "Unknown"}</div>
                        <div class="text-muted-foreground text-xs">
                          {c.contactName ? c.number : c.incoming ? "Incoming" : "Outgoing"}
                        </div>
                      </div>
                      <ToneBadge
                        tone={
                          c.state === CallStateKind.CALL_STATE_RINGING
                            ? "warn"
                            : c.state === CallStateKind.CALL_STATE_ACTIVE
                              ? "good"
                              : "neutral"
                        }
                      >
                        {stateName(c.state)}
                      </ToneBadge>
                    </div>
                    <div class="flex flex-wrap gap-1.5">
                      <Show when={c.state === CallStateKind.CALL_STATE_RINGING}>
                        <Button
                          size="sm"
                          class="bg-success hover:bg-success/90 text-white"
                          onClick={() => op({ case: "answer", value: { $typeName: "fistos.v1.Answer" } }, c.callId)}
                        >
                          <Phone /> Answer
                        </Button>
                      </Show>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => op({ case: "hangup", value: { $typeName: "fistos.v1.Hangup" } }, c.callId)}
                      >
                        <PhoneOff /> {c.state === CallStateKind.CALL_STATE_RINGING ? "Decline" : "Hang up"}
                      </Button>
                      <Show
                        when={
                          c.state === CallStateKind.CALL_STATE_ACTIVE || c.state === CallStateKind.CALL_STATE_HOLDING
                        }
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            op(
                              {
                                case: "hold",
                                value: {
                                  $typeName: "fistos.v1.Hold",
                                  on: c.state !== CallStateKind.CALL_STATE_HOLDING,
                                },
                              },
                              c.callId,
                            )
                          }
                        >
                          <Show
                            when={c.state === CallStateKind.CALL_STATE_HOLDING}
                            fallback={
                              <>
                                <Pause /> Hold
                              </>
                            }
                          >
                            <Play /> Resume
                          </Show>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            if (await op({ case: "mute", value: { $typeName: "fistos.v1.Mute", on: !muted() } }))
                              setMuted(!muted());
                          }}
                        >
                          <Show
                            when={muted()}
                            fallback={
                              <>
                                <MicOff /> Mute
                              </>
                            }
                          >
                            <Mic /> Unmute
                          </Show>
                        </Button>
                      </Show>
                    </div>
                  </div>
                )}
              </For>
            </Show>
            <Show when={recent().length}>
              <div class="grid gap-1 *:min-w-0">
                <div class="text-muted-foreground text-xs font-medium">Recent call events</div>
                <For each={recent()}>
                  {(r) => (
                    <div class="text-muted-foreground flex justify-between text-xs">
                      <span class="truncate">
                        {r.c.contactName || r.c.number || "Unknown"} · {stateName(r.c.state)}
                      </span>
                      <span class="tabular-nums">{clock(r.at)}</span>
                    </div>
                  )}
                </For>
              </div>
            </Show>
          </CardContent>
        </Card>

        <div class="grid content-start gap-4">
          <Card class="gap-4 py-5">
            <CardHeader class="px-5">
              <CardTitle class="text-base">Dial</CardTitle>
              <CardDescription>
                Calls to numbers that are not in the phone's contacts wait for your approval.
              </CardDescription>
            </CardHeader>
            <CardContent class="grid gap-3 px-5 *:min-w-0">
              <form class="flex gap-2" onSubmit={dial}>
                <TextField class="flex-1" value={number()} onChange={setNumber}>
                  <TextFieldInput type="tel" placeholder="+1 555 0100" />
                </TextField>
                <Button type="submit" disabled={!props.device.online || !number().trim()}>
                  <Phone /> Call
                </Button>
              </form>
              <div class="grid grid-cols-6 gap-1.5 sm:grid-cols-12 lg:grid-cols-6">
                <For each={KEYS}>
                  {(k) => (
                    <Button
                      variant="outline"
                      size="sm"
                      class="font-mono"
                      disabled={!calls().length}
                      onClick={() => dtmf(k)}
                      aria-label={`Tone ${k}`}
                    >
                      {k}
                    </Button>
                  )}
                </For>
              </div>
              <p class="text-muted-foreground text-xs">Keypad tones go to the active call.</p>
            </CardContent>
          </Card>

          <Card class="gap-4 py-5">
            <CardHeader class="px-5">
              <CardTitle class="flex items-center gap-2 text-base">
                <Headphones class="size-4" /> Listen
              </CardTitle>
              <CardDescription>
                <Show when={downlink() !== "none"} fallback="This phone cannot capture call audio.">
                  Relay the other party's audio here
                  {downlink() === "acoustic"
                    ? " (speakerphone + microphone fallback: lower quality, audible in the room)."
                    : "."}
                </Show>
              </CardDescription>
            </CardHeader>
            <CardContent class="grid gap-3 px-5 *:min-w-0">
              <Button
                variant={listening() ? "secondary" : "outline"}
                disabled={downlink() === "none" || !props.device.online}
                onClick={() => listen(!listening())}
              >
                <Headphones /> {listening() ? "Stop listening" : "Start listening"}
              </Button>
              <p class="text-muted-foreground text-xs">
                <Show
                  when={props.caps?.callUplinkInjection}
                  fallback="Speaking into the call from here is not supported on this phone; use call forwarding to a VoIP number or speak at the handset."
                >
                  This phone supports speaking into calls (uplink injection).
                </Show>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </Show>
  );
}
